import { widgets } from '@/helpers/consts';

export const THEME_APP_BLOCK_CONFIG = {
  [widgets.productReviewWidget]: [
    {
      handle: 'product-widget',
      type: 'block',
      template: 'product',
      target: 'newAppsSection',
      labelKey: 'Widgets.addToTheme',
    },
  ],
  [widgets.stars]: [
    {
      handle: 'product-stars',
      type: 'block',
      template: 'product',
      target: 'mainSection',
      labelKey: 'Widgets.enableProductStars',
    },
    {
      handle: 'list-stars',
      type: 'block',
      template: 'collection',
      target: 'mainSection',
      labelKey: 'Widgets.enableCollectionStars',
    },
  ],
  [widgets.carousel]: [
    {
      handle: 'carousel',
      type: 'block',
      template: 'index',
      target: 'newAppsSection',
      labelKey: 'Widgets.addToTheme',
    },
  ],
  [widgets.footerEmailBadge]: [
    {
      handle: 'footer-badge',
      type: 'block',
      template: 'index',
      target: 'footer',
      labelKey: 'Widgets.addToTheme',
    },
  ],
  [widgets.floatingWidget]: [
    {
      handle: 'global',
      type: 'embed',
      labelKey: 'Widgets.enableInThemeEditor',
    },
  ],
  [widgets.allReviewsPage]: [
    {
      handle: 'all-reviews',
      type: 'block',
      target: 'newAppsSection',
      labelKey: 'Widgets.addToTheme',
    },
  ],
  richSnippet: [
    {
      handle: 'rich-snippet',
      type: 'embed',
      labelKey: 'Widgets.enableInThemeEditor',
    },
  ],
};

export function parseThemePlacementTarget(target) {
  if (!target || typeof target !== 'string' || target === 'theme') {
    return {};
  }

  const parts = target.split('/').filter(Boolean);
  if (!parts.length) {
    return {};
  }

  const template = parts[0].startsWith('template--') ? parts[0].slice('template--'.length) : null;

  if (template && parts.length >= 3) {
    return {
      template,
      sectionId: parts[1],
      blockId: parts.slice(2).join('/'),
    };
  }

  return template ? { template } : {};
}

function themeEditorBase(shopDomain) {
  return `https://${shopDomain}/admin/themes/current/editor`;
}

export function buildExistingThemeBlockUrl({ shopDomain, type, template, placement }) {
  const base = themeEditorBase(shopDomain);

  if (type === 'embed') {
    return `${base}?context=apps`;
  }

  const parsed = parseThemePlacementTarget(placement && placement.target);
  const params = [];
  const templateName = parsed.template || template;
  if (templateName) {
    params.push(`template=${encodeURIComponent(templateName)}`);
  }
  if (parsed.sectionId) {
    params.push(`section=${encodeURIComponent(parsed.sectionId)}`);
  }
  if (parsed.blockId) {
    params.push(`block=${encodeURIComponent(parsed.blockId)}`);
  }

  return params.length ? `${base}?${params.join('&')}` : base;
}

export function buildThemeAppBlockUrl({ shopDomain, apiKey, handle, type, template, target }) {
  const base = themeEditorBase(shopDomain);

  if (type === 'embed') {
    return `${base}?context=apps&activateAppId=${apiKey}/${handle}`;
  }

  const params = [];
  if (template) {
    params.push(`template=${template}`);
  }
  params.push(`addAppBlockId=${apiKey}/${handle}`);
  if (target) {
    params.push(`target=${target}`);
  }

  return `${base}?${params.join('&')}`;
}

export function getThemeAppBlockLinks({ widgetKey, shopDomain, apiKey, t }) {
  if (!widgetKey || !shopDomain || !apiKey) {
    return [];
  }

  const configs = THEME_APP_BLOCK_CONFIG[widgetKey];
  if (!configs) {
    return [];
  }

  return configs.map(config => ({
    handle: config.handle,
    label: t(config.labelKey),
    url: buildThemeAppBlockUrl({ shopDomain, apiKey, ...config }),
  }));
}

/**
 * Blocks already placed in the theme must not use addAppBlockId.
 * Those links open the existing block instead.
 *
 * @param {Array<{ handle: string, label: string, url: string }>} links
 * @param {Array<{ handle: string, presentInTheme?: boolean, placements?: Array<{ target: string }> }>} blocks
 * @param {boolean} themeStatusAvailable
 * @param {{ shopDomain?: string, openLabel?: string }} [options]
 */
export function linksThatWouldNotDuplicate(links, blocks, themeStatusAvailable, options = {}) {
  if (!themeStatusAvailable) {
    return links || [];
  }

  const byHandle = new Map((blocks || []).map(block => [block.handle, block]));
  const source = links || [];
  const multiple = source.length > 1;

  return source.map(link => {
    const block = byHandle.get(link.handle);
    if (!block || !block.presentInTheme) {
      return { ...link, alreadyOnTheme: false };
    }

    const config = getThemeAppBlockConfigByHandle(link.handle);
    const placement = (block.placements || [])[0];
    const openLabel = options.openLabel || 'Open in theme editor';
    return {
      ...link,
      alreadyOnTheme: true,
      label: multiple ? `${openLabel} (${link.label})` : openLabel,
      url:
        config && options.shopDomain
          ? buildExistingThemeBlockUrl({
              shopDomain: options.shopDomain,
              ...config,
              placement,
            })
          : link.url,
    };
  });
}

export function getThemeAppBlockConfigByHandle(handle) {
  return Object.values(THEME_APP_BLOCK_CONFIG)
    .flat()
    .find(config => config.handle === handle);
}

export function getThemeAppBlockDeepLink(handle, shopDomain, apiKey, block) {
  const config = getThemeAppBlockConfigByHandle(handle);
  if (!config || !shopDomain || !apiKey) {
    return null;
  }

  if (block && block.presentInTheme) {
    return buildExistingThemeBlockUrl({
      shopDomain,
      ...config,
      placement: (block.placements || [])[0],
    });
  }

  return buildThemeAppBlockUrl({ shopDomain, apiKey, ...config });
}
